/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectCurrency, selectLocale } from '~/components/account/ducks';
import FlowBlocker from '~/components/authentication/flow-blocker';
import { limitsConst } from '~/components/payment/common/lib/utils/constants';
import { skipAmountSuggestion } from '~/components/payment-beta/utils';
import SchufaCreditLimitBanner from '~/components/payment/deposit/components/schufa-credit-limit-banner';
import {
	API_ERROR,
	API_LOADING,
	API_SUCCESS,
	LOADING,
	textKeysQDContent as textKeys
} from '~/components/payment/quick-deposit-beta/utils/constants';
import { providerLimitInfo, getLimitType } from '~/components/payment/quick-deposit-beta/utils/qd-helpers';
import {
	useInstantDepositInfo,
	useQDNotifications
} from '~/components/payment/quick-deposit-beta/utils/use-quick-deposit-hooks';
import useTranslations from '~/components/translation/use-translations';
import { useCloseCurrentContext } from '~/hooks/use-close-context';
import usePaymentTracking from '~/components/payment-beta/deposit/hooks/use-payment-tracking';

import NewQDAmountContainer from './deposit-amount';
import NewQDKYCScreen from './kyc';
import MewQDSOWScreen from './sow';
import NewQDRGScreen from './rg-screen';
import { Content, Wrapper } from './styles';

const NewQDMainContent = ({
	addView,
	hideView,
	views,
	updateViews,
	setKyc,
	redirectToPayments,
	redirectToSow,
	updateExecuteResult
}) => {
	const locale = useSelector(selectLocale);
	const currency = useSelector(selectCurrency);
	const skipSuggestedAmount = skipAmountSuggestion();
	const instantDepositData = useInstantDepositInfo();
	const closeCurrentContext = useCloseCurrentContext();
	const { showErrorNotification } = useQDNotifications();
	const { isLoadingTranslations, translations: t } = useTranslations(textKeys);
	const { trackQDEvent } = usePaymentTracking();

	const [updateAmountVal, setAmount] = useState('');
	const [selectedMethod, setSelectedMethod] = useState(null);
	const [showKYCMessage, setShowKYCMessage] = useState(false);
	const [crossLimitAttempted, setCrossLimit] = useState(false);
	const [showRGMessageLimit, setRGMessageLimit] = useState(false);
	const [lugasLimit, setLugasLimit] = useState(false);
	const [providerLimitsData, setProviderLimitsData] = useState({});
	const [schufaError, setSchufaError] = useState('');

	const { depositInfo, depositInfoStatus, error: infoError } = instantDepositData;
	const defaultSelected = depositInfo?.instantDepositInfoForMethods?.filter(item => item.selected)[0];
	const defaultProvider = defaultSelected || depositInfo?.instantDepositInfoForMethods[0];

	const handleModalClose = () => {
		// Track modal close action
		trackQDEvent({
			pixel_deposit_action: 'click-close-quick-deposit-modal'
		});
		closeCurrentContext();
	};

	const handleRGClose = event => {
		if (event) event.stopPropagation();

		if (crossLimitAttempted) {
			// Track close action when cross limit attempted
			const limitType = getLimitType(providerLimitsData?.responsibleGamingLimitType);
			trackQDEvent({
				pixel_deposit_action: `click-x-${limitType}-limits-reached-popup`
			});

			setCrossLimit(false);
			setRGMessageLimit(false);
			setAmount(providerLimitsData?.sum_max);
		} else {
			// Track close action based on the current screen
			let closeAction = 'click-close-quick-deposit-modal';

			if (showRGMessageLimit && limitsConst[providerLimitsData?.responsibleGamingLimitType] !== 7) {
				const limitType = getLimitType(providerLimitsData?.responsibleGamingLimitType);
				closeAction = `click-x-${limitType}-limits-reached-popup`;
			} else if (
				providerLimitsData?.sum_max === 0 &&
				limitsConst[providerLimitsData?.responsibleGamingLimitType] === 7
			) {
				closeAction = 'click-x-sow-popup';
			}

			trackQDEvent({
				pixel_deposit_action: closeAction
			});

			closeCurrentContext();
		}
	};

	useEffect(() => {
		if (showKYCMessage) {
			// Track KYC popup appearance
			trackQDEvent({
				pixel_deposit_action: 'upload-document-popup-appeared'
			});
			return setKyc(selectedMethod?.depositKycData);
		}
	}, [showKYCMessage, setKyc]);

	useEffect(() => {
		const selectedMethodLimit = selectedMethod?.depositLimitsAndFeesData;

		setProviderLimitsData(providerLimitInfo(selectedMethodLimit, currency, locale));
	}, [selectedMethod]);

	useEffect(() => {
		if (isLoadingTranslations || depositInfoStatus === API_LOADING) {
			updateViews(addView(LOADING));
		} else {
			updateViews(hideView(LOADING));
		}
	}, [isLoadingTranslations, depositInfoStatus]);

	useEffect(() => {
		if (depositInfoStatus === API_SUCCESS) {
			// eslint-disable-next-line camelcase
			const { sum_min } = providerLimitsData;
			const { suggestedIncreasedAmount, lastDepositAmountPlayerCurrency } = depositInfo || {};
			const suggestedAmount = skipSuggestedAmount ? null : suggestedIncreasedAmount;
			const lastDepositAmount = suggestedAmount || lastDepositAmountPlayerCurrency;
			const lastDepositVal = Number(lastDepositAmount);

			setSelectedMethod(defaultProvider);
			// eslint-disable-next-line camelcase
			setAmount(lastDepositVal < sum_min ? sum_min : lastDepositVal);

			trackQDEvent({
				pixel_last_payment_method: defaultProvider?.paymentMethodName,
				pixel_depositAmount: lastDepositAmount
			});
		}

		if (depositInfoStatus === API_ERROR) {
			const errMsg = infoError || t.SYSTEM_ERROR_GENERAL;

			showErrorNotification(errMsg);
		}
	}, [depositInfoStatus]);

	const renderContent = () => {
		switch (true) {
			case showKYCMessage:
				return (
					<NewQDKYCScreen
						addView={addView}
						setShowKYCMessage={setShowKYCMessage}
						translation={t}
						updateViews={updateViews}
					/>
				);

			case providerLimitsData?.sum_max === 0 && limitsConst[providerLimitsData?.responsibleGamingLimitType] === 7:
				return <MewQDSOWScreen translation={t} handleClose={handleRGClose} redirectToSow={redirectToSow} />;

			case showRGMessageLimit && limitsConst[providerLimitsData?.responsibleGamingLimitType] !== 7:
				return (
					<NewQDRGScreen
						addView={addView}
						updateViews={updateViews}
						formattedLimit={providerLimitsData?.formattedLimit}
						handleClose={handleRGClose}
						isLugasLimit={lugasLimit}
						translation={t}
						responsibleGamingLimitType={providerLimitsData?.responsibleGamingLimitType}
					/>
				);

			default:
				return (
					<NewQDAmountContainer
						views={views}
						addView={addView}
						hideView={hideView}
						updateViews={updateViews}
						setCrossLimit={setCrossLimit}
						selectedMethod={selectedMethod}
						updateAmountVal={updateAmountVal}
						setSelectedMethod={setSelectedMethod}
						setShowKYCMessage={setShowKYCMessage}
						setRGMessageState={setRGMessageLimit}
						setSchufaError={setSchufaError}
						setLugasLimit={setLugasLimit}
						updateExecuteResult={updateExecuteResult}
						instantDepositData={instantDepositData}
						providerLimitsData={providerLimitsData}
						redirectToPayments={redirectToPayments}
					/>
				);
		}
	};

	return (
		<FlowBlocker onCancel={handleModalClose}>
			{schufaError || providerLimitsData?.schufaLimitHit ? (
				<SchufaCreditLimitBanner
					schufaError={schufaError}
					setSchufaError={setSchufaError}
					onclose={handleModalClose}
				/>
			) : (
				<Wrapper data-at="new-qd-main-content">
					<Content title="main-content" className="u-fixed">
						{renderContent()}
					</Content>
				</Wrapper>
			)}
		</FlowBlocker>
	);
};

export default NewQDMainContent;
